'use client';

import { mergeProps } from '@base-ui/react/merge-props';
import { useRender } from '@base-ui/react/use-render';
import { cva, type VariantProps } from 'class-variance-authority';
import { PanelLeft } from 'lucide-react';
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipPopup, TooltipTrigger } from '@/components/ui/tooltip';
import { useIsMobile } from '@/hooks/use-mobile';
import { createSafeContext } from '@/lib/create-safe-context';
import { cn } from '@/lib/utils';

const SIDEBAR_COOKIE_NAME = 'sidebar_state';
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = '324px';
const SIDEBAR_WIDTH_MOBILE = '18rem';
// Collapsed width: 76px content + 16px padding + 16px spacing-xl = 108px
const SIDEBAR_WIDTH_ICON = '108px';
const SIDEBAR_KEYBOARD_SHORTCUT = 'b';

type SidebarContextProps = {
  state: 'expanded' | 'collapsed';
  open: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
  /** Whether the sidebar is in connected layout mode */
  connected: boolean;
  /** Whether the sidebar is in a loading state */
  loading: boolean;
  /** Custom background color class for the content/inset area */
  contentBgColor?: string;
  /** Ref to the visible sidebar rail element. Use to anchor flyouts/popovers to the sidebar edge. */
  containerRef: React.RefObject<HTMLDivElement | null>;
};

const [SidebarContext, useSidebar] =
  createSafeContext<SidebarContextProps>('SidebarProvider');

function SidebarProvider({
  defaultOpen = false,
  open: openProp,
  onOpenChange: setOpenProp,
  connected = true,
  loading = false,
  contentBgColor,
  className,
  style,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Enable connected layout mode for use inside AppShell.
   *
   * In connected mode:
   * - Sidebar fills its parent container (no fixed positioning)
   * - Width is controlled by the parent container
   * - Collapse/expand is not supported (sidebar always visible)
   * - The `side` prop is handled by DOM order
   */
  connected?: boolean;
  /** Whether the sidebar is in a loading state */
  loading?: boolean;
  /** Custom background color class for the content/inset area (e.g., 'bg-destructive') */
  contentBgColor?: string;
}) {
  const isMobile = useIsMobile();
  const [openMobile, setOpenMobile] = React.useState(false);
  // Ref for portal-proof document/window access.
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  // This is the internal state of the sidebar.
  // We use openProp and setOpenProp for control from outside the component.
  const [_open, _setOpen] = React.useState(defaultOpen);
  const open = openProp ?? _open;
  const openRef = React.useRef(open);
  openRef.current = open;
  const setOpen = React.useCallback(
    (value: boolean | ((value: boolean) => boolean)) => {
      const openState =
        typeof value === 'function' ? value(openRef.current) : value;
      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        _setOpen(openState);
      }

      // This sets the cookie to keep the sidebar state.
      const ownerDocument = wrapperRef.current?.ownerDocument;
      if (ownerDocument) {
        ownerDocument.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
      }
    },
    [setOpenProp],
  );

  // Helper to toggle the sidebar.
  const toggleSidebar = React.useCallback(() => {
    return isMobile ? setOpenMobile((open) => !open) : setOpen((open) => !open);
  }, [isMobile, setOpen]);

  // Ref to the visible sidebar rail element (set by Sidebar). Exposed via context for anchoring flyouts.
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  // Adds a keyboard shortcut to toggle the sidebar.
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault();
        toggleSidebar();
      }
    };

    // Use ownerDocument.defaultView for portal/iframe compatibility (ADR-010)
    const win = wrapperRef.current?.ownerDocument.defaultView;
    if (!win) return;
    win.addEventListener('keydown', handleKeyDown);
    return () => win.removeEventListener('keydown', handleKeyDown);
  }, [toggleSidebar]);

  // Set CSS variables on :root when mobile state changes
  // Use concrete values so consumers can use these variables globally
  React.useEffect(() => {
    const root = wrapperRef.current?.ownerDocument.documentElement;
    if (!root) return;

    // Set global sidebar width values for consumer use
    root.style.setProperty('--sidebar-width', SIDEBAR_WIDTH);
    root.style.setProperty('--sidebar-width-mobile', SIDEBAR_WIDTH_MOBILE);
    root.style.setProperty('--sidebar-width-icon', SIDEBAR_WIDTH_ICON);

    if (isMobile) {
      root.style.setProperty('--sidebar-mobile', '1');
      root.style.setProperty('--sidebar-width-current', SIDEBAR_WIDTH_MOBILE);
      root.dataset.sidebarMobile = 'true';
    } else {
      root.style.setProperty('--sidebar-mobile', '0');
      root.style.setProperty('--sidebar-width-current', SIDEBAR_WIDTH);
      root.dataset.sidebarMobile = 'false';
    }

    return () => {
      root.style.removeProperty('--sidebar-mobile');
      root.style.removeProperty('--sidebar-width');
      root.style.removeProperty('--sidebar-width-mobile');
      root.style.removeProperty('--sidebar-width-icon');
      root.style.removeProperty('--sidebar-width-current');
      delete root.dataset.sidebarMobile;
    };
  }, [isMobile]);

  // We add a state so that we can do data-state="expanded" or "collapsed".
  // This makes it easier to style the sidebar with Tailwind classes.
  const state = open ? 'expanded' : 'collapsed';

  const contextValue = React.useMemo<SidebarContextProps>(
    () => ({
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
      connected,
      loading,
      contentBgColor,
      containerRef,
    }),
    [
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      toggleSidebar,
      connected,
      loading,
      contentBgColor,
    ],
  );

  return (
    <SidebarContext.Provider value={contextValue}>
      <div
        ref={wrapperRef}
        data-slot="sidebar-wrapper"
        style={
          {
            '--sidebar-width': SIDEBAR_WIDTH,
            '--sidebar-width-icon': SIDEBAR_WIDTH_ICON,
            ...style,
          } as React.CSSProperties
        }
        className={cn(
          'group/sidebar-wrapper flex w-full',
          // Only impose min-h-svh in default mode; connected mode inherits height from parent
          connected ? 'h-full' : 'min-h-svh',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </SidebarContext.Provider>
  );
}
SidebarProvider.displayName = 'SidebarProvider';

/**
 * Sidebar is a responsive navigation container.
 * Supports mobile sheet mode, keyboard shortcuts, and persistent state via cookies.
 *
 * @llms Responsive sidebar with mobile sheet mode
 * @example
 * ```tsx
 * <SidebarProvider>
 *   <Sidebar collapsible="icon">
 *     <SidebarHeader />
 *     <SidebarContent>...</SidebarContent>
 *     <SidebarFooter />
 *   </Sidebar>
 * </SidebarProvider>
 * ```
 */
function Sidebar({
  side = 'left',
  collapsible = 'offcanvas',
  width = SIDEBAR_WIDTH,
  collapsedWidth = SIDEBAR_WIDTH_ICON,
  mobileWidth = SIDEBAR_WIDTH_MOBILE,
  offsetTop = '0px',
  offsetBottom = '0px',
  rounded = false,
  className,
  style,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  side?: 'left' | 'right';
  collapsible?: 'offcanvas' | 'icon' | 'none';
  width?: string;
  collapsedWidth?: string;
  mobileWidth?: string;
  offsetTop?: string;
  offsetBottom?: string;
  rounded?: boolean;
}) {
  const {
    isMobile,
    state,
    openMobile,
    setOpenMobile,
    connected,
    containerRef,
  } = useSidebar();

  const roundedClass = React.useMemo(() => {
    if (!rounded) return '';
    // Inner edge: the edge facing content (left sidebar → right edge, right sidebar → left edge)
    return side === 'left'
      ? 'rounded-r-(--sidebar-radius,0.5rem)'
      : 'rounded-l-(--sidebar-radius,0.5rem)';
  }, [rounded, side]);

  // Connected mode: simplified layout for use inside AppShell
  if (connected) {
    return (
      <div
        ref={containerRef}
        data-slot="sidebar"
        data-state={state}
        data-collapsible={state === 'collapsed' ? collapsible : ''}
        data-connected="true"
        className={cn(
          'group text-sidebar-foreground flex h-full w-full flex-col overflow-hidden',
          className,
        )}
        style={
          {
            '--sidebar-width': width,
            '--sidebar-width-icon': collapsedWidth,
            ...style,
          } as React.CSSProperties
        }
        {...props}
      >
        {children}
      </div>
    );
  }

  if (collapsible === 'none') {
    return (
      <div
        data-slot="sidebar"
        className={cn(
          'bg-sidebar text-sidebar-foreground flex h-full w-(--sidebar-width) flex-col',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    );
  }

  if (isMobile) {
    return (
      <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
        <SheetContent
          data-sidebar="sidebar"
          data-slot="sidebar"
          data-mobile="true"
          className="bg-sidebar text-sidebar-foreground w-(--sidebar-width) p-0 [&>button]:hidden"
          style={
            {
              '--sidebar-width': mobileWidth,
            } as React.CSSProperties
          }
          side={side}
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Sidebar</SheetTitle>
            <SheetDescription>Displays the mobile sidebar.</SheetDescription>
          </SheetHeader>
          <div className="flex h-full w-full flex-col">{children}</div>
        </SheetContent>
      </Sheet>
    );
  }

  const effectiveCollapsible = state === 'collapsed' ? collapsible : '';
  const {
    '--sidebar-width': resolvedWidth = width,
    '--sidebar-width-icon': resolvedCollapsedWidth = collapsedWidth,
    ...containerStyle
  } = (style ?? {}) as React.CSSProperties & {
    '--sidebar-width'?: string;
    '--sidebar-width-icon'?: string;
  };

  return (
    <div
      className="group peer text-sidebar-foreground hidden md:block"
      data-state={state}
      data-collapsible={effectiveCollapsible}
      data-side={side}
      data-slot="sidebar"
      style={
        {
          '--sidebar-width': resolvedWidth,
          '--sidebar-width-icon': resolvedCollapsedWidth,
        } as React.CSSProperties
      }
    >
      {/* The gap and fixed rail share width values and the same transition period. */}
      <div
        data-slot="sidebar-gap-container"
        className={cn(
          'grid motion-safe:transition-[grid-template-columns] motion-safe:duration-(--duration-slow) motion-safe:ease-decelerate motion-reduce:transition-none',
          'grid-cols-[var(--sidebar-width)]',
          'group-data-[collapsible=offcanvas]:grid-cols-[0px]',
          'group-data-[side=right]:rotate-180',
          'group-data-[collapsible=icon]:grid-cols-[var(--sidebar-width-icon)]',
        )}
      >
        <div
          data-slot="sidebar-gap"
          className="overflow-hidden bg-transparent"
        />
      </div>
      <div
        ref={containerRef}
        data-slot="sidebar-container"
        inert={effectiveCollapsible === 'offcanvas' || undefined}
        aria-hidden={effectiveCollapsible === 'offcanvas' || undefined}
        style={
          {
            '--sidebar-offset-top': offsetTop,
            '--sidebar-offset-bottom': offsetBottom,
            ...containerStyle,
          } as React.CSSProperties
        }
        className={cn(
          'fixed z-10 hidden motion-safe:transition-[left,right,width,height,top,bottom] motion-safe:duration-(--duration-slow) motion-safe:ease-decelerate motion-reduce:transition-none md:flex',
          'w-(--sidebar-width)',
          'top-[var(--sidebar-offset-top)] bottom-[var(--sidebar-offset-bottom)] h-[calc(100svh-var(--sidebar-offset-top)-var(--sidebar-offset-bottom))]',
          side === 'left'
            ? 'left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]'
            : 'right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]',
          'group-data-[collapsible=icon]:w-(--sidebar-width-icon)',
          className,
        )}
        {...props}
      >
        <div
          data-sidebar="sidebar"
          data-slot="sidebar-inner"
          className={cn('flex size-full flex-col', roundedClass)}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
Sidebar.displayName = 'Sidebar';

function SidebarTrigger({
  className,
  onClick,
  ...props
}: React.ComponentProps<typeof Button>) {
  const { toggleSidebar, open } = useSidebar();

  return (
    <Button
      data-sidebar="trigger"
      data-slot="sidebar-trigger"
      appearance="ghost"
      variant="secondary"
      size="icon-sm"
      aria-label="Toggle sidebar"
      aria-expanded={open}
      className={cn(className)}
      onClick={(event) => {
        onClick?.(event);
        toggleSidebar();
      }}
      {...props}
    >
      <PanelLeft aria-hidden="true" />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  );
}
SidebarTrigger.displayName = 'SidebarTrigger';

function SidebarRail({ className, ...props }: React.ComponentProps<'button'>) {
  const { toggleSidebar } = useSidebar();

  return (
    <button
      data-sidebar="rail"
      data-slot="sidebar-rail"
      aria-label="Toggle Sidebar"
      tabIndex={-1}
      onClick={toggleSidebar}
      title="Toggle Sidebar"
      className={cn(
        'hover:after:bg-sidebar-border absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 transition-all ease-linear motion-reduce:transition-none group-data-[side=left]:-right-4 group-data-[side=right]:left-0 after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] sm:flex',
        'in-data-[side=left]:cursor-w-resize in-data-[side=right]:cursor-e-resize',
        '[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize',
        'hover:group-data-[collapsible=offcanvas]:bg-sidebar group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full',
        '[[data-side=left][data-collapsible=offcanvas]_&]:-right-2',
        '[[data-side=right][data-collapsible=offcanvas]_&]:-left-2',
        className,
      )}
      {...props}
    />
  );
}
SidebarRail.displayName = 'SidebarRail';

function SidebarInset({
  className,
  connected: connectedProp,
  contentBgColor: contentBgColorProp,
  ...props
}: React.ComponentProps<'main'> & {
  /** Enable connected layout mode directly, without needing SidebarProvider */
  connected?: boolean;
  /** Custom background color class (e.g., 'bg-destructive'). Overrides context value. */
  contentBgColor?: string;
}) {
  const context = React.useContext(SidebarContext);
  // Prefer prop over context, fallback to false
  const connected = connectedProp ?? context?.connected ?? false;
  const isMobile = context?.isMobile ?? false;
  // Prefer prop over context, fallback to 'bg-background'
  const bgColor =
    contentBgColorProp ?? context?.contentBgColor ?? 'bg-background';

  // Connected mode: simplified layout for use inside AppShell
  if (connected) {
    return (
      <main
        data-slot="sidebar-inset"
        data-connected="true"
        data-mobile={isMobile ? 'true' : 'false'}
        className={cn(
          'relative flex h-full w-full flex-1 flex-col overflow-hidden',
          bgColor,
          isMobile && 'p-0',
          className,
        )}
        {...props}
      />
    );
  }

  return (
    <main
      data-slot="sidebar-inset"
      data-mobile={isMobile ? 'true' : 'false'}
      className={cn(bgColor, 'relative flex w-full flex-1 flex-col', className)}
      {...props}
    />
  );
}
SidebarInset.displayName = 'SidebarInset';

function SidebarInput({
  className,
  ...props
}: React.ComponentProps<typeof Input>) {
  return (
    <Input
      data-slot="sidebar-input"
      data-sidebar="input"
      className={cn('bg-background h-8 w-full shadow-none', className)}
      {...props}
    />
  );
}
SidebarInput.displayName = 'SidebarInput';

function SidebarHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-header"
      data-sidebar="header"
      className={cn(
        'gap-[var(--sidebar-gap,0.5rem)] p-[var(--sidebar-padding-inner,0.5rem)] flex shrink-0 flex-col',
        className,
      )}
      {...props}
    />
  );
}
SidebarHeader.displayName = 'SidebarHeader';

function SidebarFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-footer"
      data-sidebar="footer"
      className={cn(
        'gap-[var(--sidebar-gap,0.5rem)] p-[var(--sidebar-padding-inner,0.5rem)] flex shrink-0 flex-col',
        className,
      )}
      {...props}
    />
  );
}
SidebarFooter.displayName = 'SidebarFooter';

function SidebarSeparator({
  className,
  ...props
}: React.ComponentProps<typeof Separator>) {
  return (
    <Separator
      data-slot="sidebar-separator"
      data-sidebar="separator"
      className={cn('bg-sidebar-border mx-2 w-auto', className)}
      {...props}
    />
  );
}
SidebarSeparator.displayName = 'SidebarSeparator';

function SidebarContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-content"
      data-sidebar="content"
      className={cn(
        'no-scrollbar gap-[var(--sidebar-gap,0.5rem)] flex min-h-0 flex-1 flex-col overflow-auto group-data-[collapsible=icon]:overflow-hidden',
        className,
      )}
      {...props}
    />
  );
}
SidebarContent.displayName = 'SidebarContent';

function SidebarGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-group"
      data-sidebar="group"
      className={cn(
        'p-[var(--sidebar-padding-inner,0.5rem)] relative flex w-full min-w-0 flex-col',
        className,
      )}
      {...props}
    />
  );
}
SidebarGroup.displayName = 'SidebarGroup';

function SidebarGroupLabel({
  className,
  render,
  ...props
}: useRender.ComponentProps<'div'> & React.ComponentProps<'div'>) {
  return useRender({
    defaultTagName: 'div',
    props: mergeProps<'div'>(
      {
        className: cn(
          'text-sidebar-foreground/80 ring-sidebar-ring h-8 rounded-md px-2 text-xs font-medium transition-[transform,opacity] duration-200 ease-linear motion-reduce:transition-none group-data-[collapsible=icon]:-translate-y-8 group-data-[collapsible=icon]:opacity-0 focus-visible:ring-2 [&>svg]:size-4 flex shrink-0 items-center outline-hidden [&>svg]:shrink-0',
          className,
        ),
      },
      props,
    ),
    render,
    state: {
      slot: 'sidebar-group-label',
      sidebar: 'group-label',
    },
  });
}
SidebarGroupLabel.displayName = 'SidebarGroupLabel';

function SidebarGroupAction({
  className,
  render,
  ...props
}: useRender.ComponentProps<'button'> & React.ComponentProps<'button'>) {
  return useRender({
    defaultTagName: 'button',
    props: mergeProps<'button'>(
      {
        className: cn(
          'text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground absolute top-3.5 end-3 w-5 rounded-md p-0 focus-visible:ring-2 [&>svg]:size-4 flex aspect-square items-center justify-center outline-hidden transition-transform motion-reduce:transition-none [&>svg]:shrink-0 after:absolute after:-inset-2 group-data-[collapsible=icon]:hidden',
          className,
        ),
      },
      props,
    ),
    render,
    state: {
      slot: 'sidebar-group-action',
      sidebar: 'group-action',
    },
  });
}
SidebarGroupAction.displayName = 'SidebarGroupAction';

function SidebarGroupContent({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-group-content"
      data-sidebar="group-content"
      className={cn('text-sm w-full', className)}
      {...props}
    />
  );
}
SidebarGroupContent.displayName = 'SidebarGroupContent';

function SidebarMenu({ className, ...props }: React.ComponentProps<'ul'>) {
  return (
    <ul
      data-slot="sidebar-menu"
      data-sidebar="menu"
      className={cn(
        'gap-[var(--sidebar-gap,0.5rem)] flex w-full min-w-0 flex-col',
        className,
      )}
      {...props}
    />
  );
}
SidebarMenu.displayName = 'SidebarMenu';

function SidebarMenuItem({ className, ...props }: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="sidebar-menu-item"
      data-sidebar="menu-item"
      className={cn('group/menu-item relative', className)}
      {...props}
    />
  );
}
SidebarMenuItem.displayName = 'SidebarMenuItem';

const sidebarMenuButtonVariants = cva(
  'ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground data-active:bg-sidebar-accent data-active:text-sidebar-accent-foreground data-open:hover:bg-sidebar-accent data-open:hover:text-sidebar-accent-foreground gap-2 rounded-md p-2 text-left text-sm transition-[width,height,padding] motion-reduce:transition-none group-has-data-[sidebar=menu-action]/menu-item:pe-8 group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2! focus-visible:ring-2 data-active:font-medium peer/menu-button flex w-full items-center overflow-hidden outline-hidden group/menu-button disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
        outline:
          'bg-background hover:bg-sidebar-accent hover:text-sidebar-accent-foreground shadow-[0_0_0_1px_var(--sidebar-border)] hover:shadow-[0_0_0_1px_var(--sidebar-accent)]',
      },
      size: {
        default: 'min-h-11 text-sm',
        sm: 'min-h-8 text-xs',
        lg: 'h-12 text-sm group-data-[collapsible=icon]:p-0!',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

function SidebarMenuButton({
  render,
  isActive = false,
  variant = 'default',
  size = 'default',
  tooltip,
  className,
  ...props
}: useRender.ComponentProps<'button'> &
  React.ComponentProps<'button'> & {
    isActive?: boolean;
    tooltip?: string | React.ComponentProps<typeof TooltipPopup>;
  } & VariantProps<typeof sidebarMenuButtonVariants>) {
  const { isMobile, state } = useSidebar();
  const comp = useRender({
    defaultTagName: 'button',
    props: mergeProps<'button'>(
      {
        className: cn(sidebarMenuButtonVariants({ variant, size }), className),
      },
      props,
    ),
    // ponytail: render + tooltip remains unsupported, as before.
    render: !tooltip
      ? render
      : (triggerProps) => <TooltipTrigger {...triggerProps} />,
    state: {
      slot: 'sidebar-menu-button',
      sidebar: 'menu-button',
      size,
      active: isActive,
    },
  });

  if (!tooltip) {
    return comp;
  }

  if (typeof tooltip === 'string') {
    tooltip = {
      children: tooltip,
    };
  }

  return (
    <Tooltip>
      {comp}
      <TooltipPopup
        side="right"
        align="center"
        hidden={state !== 'collapsed' || isMobile}
        {...tooltip}
      />
    </Tooltip>
  );
}
SidebarMenuButton.displayName = 'SidebarMenuButton';

function SidebarMenuAction({
  className,
  render,
  showOnHover = false,
  ...props
}: useRender.ComponentProps<'button'> &
  React.ComponentProps<'button'> & {
    showOnHover?: boolean;
  }) {
  return useRender({
    defaultTagName: 'button',
    props: mergeProps<'button'>(
      {
        className: cn(
          'text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground peer-hover/menu-button:text-sidebar-accent-foreground absolute top-1.5 right-1 aspect-square w-5 rounded-md p-0 peer-data-[size=default]/menu-button:top-1.5 peer-data-[size=lg]/menu-button:top-2.5 peer-data-[size=sm]/menu-button:top-1 focus-visible:ring-2 [&>svg]:size-4 flex items-center justify-center outline-hidden transition-transform motion-reduce:transition-none group-data-[collapsible=icon]:hidden after:absolute after:-inset-2 [&>svg]:shrink-0',
          showOnHover &&
            'peer-data-active/menu-button:text-sidebar-accent-foreground group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-open:opacity-100 md:opacity-0',
          className,
        ),
      },
      props,
    ),
    render,
    state: {
      slot: 'sidebar-menu-action',
      sidebar: 'menu-action',
    },
  });
}
SidebarMenuAction.displayName = 'SidebarMenuAction';

function SidebarMenuBadge({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-menu-badge"
      data-sidebar="menu-badge"
      className={cn(
        'text-sidebar-foreground peer-hover/menu-button:text-sidebar-accent-foreground peer-data-active/menu-button:text-sidebar-accent-foreground pointer-events-none absolute end-1 flex h-5 min-w-5 rounded-md px-1 text-xs font-medium peer-data-[size=default]/menu-button:top-1.5 peer-data-[size=lg]/menu-button:top-2.5 peer-data-[size=sm]/menu-button:top-1 flex items-center justify-center tabular-nums select-none group-data-[collapsible=icon]:hidden',
        className,
      )}
      {...props}
    />
  );
}
SidebarMenuBadge.displayName = 'SidebarMenuBadge';

function SidebarMenuSkeleton({
  className,
  showIcon = false,
  ...props
}: React.ComponentProps<'div'> & {
  showIcon?: boolean;
}) {
  const width = showIcon ? '72%' : '64%';

  return (
    <div
      data-slot="sidebar-menu-skeleton"
      data-sidebar="menu-skeleton"
      className={cn('h-8 gap-2 rounded-md px-2 flex items-center', className)}
      {...props}
    >
      {showIcon && (
        <Skeleton
          className="size-4 rounded-md"
          data-sidebar="menu-skeleton-icon"
        />
      )}
      <Skeleton
        className="h-4 max-w-(--skeleton-width) flex-1"
        data-sidebar="menu-skeleton-text"
        style={
          {
            '--skeleton-width': width,
          } as React.CSSProperties
        }
      />
    </div>
  );
}
SidebarMenuSkeleton.displayName = 'SidebarMenuSkeleton';

function SidebarNavSkeleton({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-nav-skeleton"
      data-sidebar="nav-skeleton"
      className={cn(
        'flex h-10 w-full items-center gap-3 rounded-md px-3 py-2',
        'group-data-[collapsible=icon]:h-11 group-data-[collapsible=icon]:w-[76px] group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:py-3',
        className,
      )}
      {...props}
    >
      <Skeleton className="size-5 shrink-0 rounded" />
      <Skeleton className="h-4 flex-1 group-data-[collapsible=icon]:hidden" />
    </div>
  );
}
SidebarNavSkeleton.displayName = 'SidebarNavSkeleton';

function SidebarAccountSkeleton({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-account-skeleton"
      data-sidebar="account-skeleton"
      className={cn('flex w-full items-center gap-2 rounded-md p-2', className)}
      {...props}
    >
      <Skeleton className="size-8 shrink-0 rounded-lg" />
      <div className="flex flex-1 flex-col gap-1 group-data-[collapsible=icon]:hidden">
        <Skeleton className="h-3.5 w-20" />
        <Skeleton className="h-3 w-16" />
      </div>
    </div>
  );
}
SidebarAccountSkeleton.displayName = 'SidebarAccountSkeleton';

function SidebarMenuSub({ className, ...props }: React.ComponentProps<'ul'>) {
  return (
    <ul
      data-slot="sidebar-menu-sub"
      data-sidebar="menu-sub"
      className={cn(
        'border-sidebar-border mx-3.5 translate-x-px gap-[var(--sidebar-gap,0.5rem)] border-l px-2.5 py-0.5 group-data-[collapsible=icon]:hidden flex min-w-0 flex-col',
        className,
      )}
      {...props}
    />
  );
}
SidebarMenuSub.displayName = 'SidebarMenuSub';

function SidebarMenuSubItem({
  className,
  ...props
}: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="sidebar-menu-sub-item"
      data-sidebar="menu-sub-item"
      className={cn('group/menu-sub-item relative', className)}
      {...props}
    />
  );
}
SidebarMenuSubItem.displayName = 'SidebarMenuSubItem';

function SidebarMenuSubButton({
  render,
  size = 'md',
  isActive = false,
  className,
  ...props
}: useRender.ComponentProps<'a'> &
  React.ComponentProps<'a'> & {
    size?: 'sm' | 'md';
    isActive?: boolean;
  }) {
  return useRender({
    defaultTagName: 'a',
    props: mergeProps<'a'>(
      {
        className: cn(
          'text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground [&>svg]:text-sidebar-accent-foreground data-active:bg-sidebar-accent data-active:text-sidebar-accent-foreground min-h-9 gap-2 rounded-md px-2 focus-visible:ring-2 data-[size=md]:text-sm data-[size=sm]:text-xs [&>svg]:size-4 flex min-w-0 -translate-x-px items-center overflow-hidden outline-hidden group-data-[collapsible=icon]:hidden disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:shrink-0',
          className,
        ),
      },
      props,
    ),
    render,
    state: {
      slot: 'sidebar-menu-sub-button',
      sidebar: 'menu-sub-button',
      size,
      active: isActive,
    },
  });
}
SidebarMenuSubButton.displayName = 'SidebarMenuSubButton';

// ============================================
// Compound Component Export
// ============================================

/**
 * Sidebar compound component with dot notation access to all parts.
 *
 * Usage:
 * - `Sidebar.Root` - Main sidebar container (alias for Sidebar)
 * - `Sidebar.Header` - Header section
 * - `Sidebar.Content` - Scrollable content area
 * - `Sidebar.Footer` - Footer section
 * - `Sidebar.Group` - Group container for menu items
 * - `Sidebar.GroupLabel` - Label for a group
 * - `Sidebar.GroupContent` - Content within a group
 * - `Sidebar.GroupAction` - Action button for a group
 * - `Sidebar.Menu` - Menu container
 * - `Sidebar.MenuItem` - Menu item wrapper
 * - `Sidebar.MenuButton` - Clickable menu button
 * - `Sidebar.MenuAction` - Action within a menu item
 * - `Sidebar.MenuBadge` - Badge within a menu item
 * - `Sidebar.MenuSkeleton` - Loading skeleton for menu
 * - `Sidebar.MenuSub` - Submenu container
 * - `Sidebar.MenuSubItem` - Submenu item
 * - `Sidebar.MenuSubButton` - Submenu button
 * - `Sidebar.Input` - Input within sidebar
 * - `Sidebar.Separator` - Visual separator
 * - `Sidebar.Rail` - Resize/toggle rail
 * - `Sidebar.Inset` - Main content inset area
 * - `Sidebar.Trigger` - Toggle button
 */
const SidebarCompound = Object.assign(Sidebar, {
  Root: Sidebar,
  Header: SidebarHeader,
  Content: SidebarContent,
  Footer: SidebarFooter,
  Group: SidebarGroup,
  GroupLabel: SidebarGroupLabel,
  GroupContent: SidebarGroupContent,
  GroupAction: SidebarGroupAction,
  Menu: SidebarMenu,
  MenuItem: SidebarMenuItem,
  MenuButton: SidebarMenuButton,
  MenuAction: SidebarMenuAction,
  MenuBadge: SidebarMenuBadge,
  MenuSkeleton: SidebarMenuSkeleton,
  MenuSub: SidebarMenuSub,
  MenuSubItem: SidebarMenuSubItem,
  MenuSubButton: SidebarMenuSubButton,
  Input: SidebarInput,
  Separator: SidebarSeparator,
  Rail: SidebarRail,
  Inset: SidebarInset,
  Trigger: SidebarTrigger,
  // Skeletons
  NavSkeleton: SidebarNavSkeleton,
  AccountSkeleton: SidebarAccountSkeleton,
});

export {
  SidebarAccountSkeleton,
  SidebarCompound as Sidebar,
  SidebarContent,
  SidebarContext,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarNavSkeleton,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
};
